import type { LocalInterview } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import type InternalInterviewListEntryView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewListEntryView';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import * as React from 'react';

type Result = {
	/** Recordings made for this preparation and not on the server yet — shown as « Non envoyé ». */
	readonly drafts: readonly InternalInterviewListEntryView[];
	/** Device-side entries of elements already on the server, by interview id: they carry the retry actions. */
	readonly entryByInterviewId: ReadonlyMap<number, InternalInterviewListEntryView>;
};

/**
 * Splits the fused interview list (server + this device) into what belongs to an open preparation.
 * Once the preparation is launched, its pending recordings move to the dossier's own section instead.
 */
const usePreparationLocalEntries = (
	preparation: WorkflowRunView,
	entries: readonly InternalInterviewListEntryView[],
	localInterviews: readonly LocalInterview[],
): Result => {
	return React.useMemo(() => {
		const elementInterviewIds = new Set(
			preparation.sources.flatMap((element) =>
				element.interviewId !== null ? [element.interviewId] : [],
			),
		);

		const entryByInterviewId = new Map<number, InternalInterviewListEntryView>();
		for (const entry of entries) {
			if (entry.interviewId !== null && entry.localInterviewId !== null) {
				entryByInterviewId.set(entry.interviewId, entry);
			}
		}

		if (!preparation.isOpen) {
			return { drafts: [], entryByInterviewId };
		}

		const drafts = entries.filter((entry) => {
			if (entry.localInterviewId === null || !(entry.isLocal || entry.isProcessing)) {
				return false;
			}

			if (entry.interviewId !== null && elementInterviewIds.has(entry.interviewId)) {
				return false;
			}

			return (
				localInterviews.find((localInterview) => localInterview.id === entry.localInterviewId)
					?.preparationId === preparation.id
			);
		});

		return { drafts, entryByInterviewId };
	}, [preparation, entries, localInterviews]);
};

export default usePreparationLocalEntries;
